import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaLayerGroup, FaRocket, FaTools, FaWordpress } from 'react-icons/fa';
import { services } from '../data/staticData';
import ServiceModal from './ServiceModal';
import { getLocalizedServices, useLanguage } from '../i18n';

const iconMap = {
    wordpress: FaWordpress,
    customize: FaLayerGroup,
    deploy: FaRocket,
    maintenance: FaTools
};

const Services = () => {
    const { language, t } = useLanguage();
    const [activeService, setActiveService] = useState(null);
    const openModal = useCallback((service) => setActiveService(service), []);
    const closeModal = useCallback(() => setActiveService(null), []);

    const process = [
        ['01', t('processDiscovery'), t('processDiscoveryDesc')],
        ['02', t('processDesign'), t('processDesignDesc')],
        ['03', t('processBuild'), t('processBuildDesc')],
        ['04', t('processHandoff'), t('processHandoffDesc')]
    ];
    const localizedServices = getLocalizedServices(language);

    return (
        <section id="services" className="bg-white py-24 lg:py-32">
            <div className="container mx-auto px-5 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="grid gap-8 border-b border-gray-200 pb-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-end"
                >
                    <div>
                        <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-primary-600">{t('servicesEyebrow')}</p>
                        <h2 className="max-w-xl text-4xl font-bold leading-tight text-ink sm:text-5xl lg:text-6xl">
                            {t('servicesTitleLead')}<span className="text-primary-600">{t('servicesTitleAccent')}</span>
                        </h2>
                    </div>
                    <p className="max-w-xl text-lg leading-8 text-muted lg:justify-self-end">
                        {t('servicesDescription')}
                    </p>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-80px' }}
                    variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
                    className="divide-y divide-gray-200"
                >
                    {localizedServices.map((service, index) => {
                        const Icon = iconMap[service.icon] || FaWordpress;
                        return (
                            <motion.article
                                key={service.id}
                                variants={{ hidden: { opacity: 0, y: 18 }, visible: { opacity: 1, y: 0 } }}
                                className="group grid gap-6 py-8 lg:grid-cols-[96px_0.85fr_1.2fr_auto] lg:items-center lg:gap-10"
                            >
                                <span className="font-display text-sm font-bold text-primary-600">0{index + 1}</span>
                                <div className="flex items-center gap-4">
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-xl text-primary-600 transition-colors group-hover:bg-primary-600 group-hover:text-white">
                                        <Icon />
                                    </span>
                                    <h3 className="font-display text-2xl font-bold text-ink transition-colors group-hover:text-primary-700">{service.title}</h3>
                                </div>
                                <div>
                                    <p className="max-w-xl leading-7 text-muted">{service.description}</p>
                                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-gray-600">
                                        {service.features.slice(0, 3).map((feature) => <span key={feature}>• {feature}</span>)}
                                    </div>
                                </div>
                                <motion.button
                                    type="button"
                                    onClick={() => openModal(service)}
                                    className="inline-flex min-h-11 items-center gap-2 justify-self-start rounded-lg border border-gray-300 px-4 text-sm font-bold text-ink transition-colors hover:border-primary-300 hover:text-primary-700 lg:justify-self-end"
                                    whileTap={{ scale: 0.96 }}
                                >
                                    {t('details')} <FaArrowRight className="text-xs text-primary-600" />
                                </motion.button>
                            </motion.article>
                        );
                    })}
                </motion.div>

                <div className="mt-20 border-y border-ink bg-ink p-8 text-white sm:p-12 lg:p-14">
                    <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
                        <div>
                            <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-primary-300">{t('workingMethod')}</p>
                            <h3 className="max-w-md text-3xl font-bold leading-tight sm:text-4xl">{t('workingMethodTitle')}</h3>
                        </div>
                        <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
                            {process.map(([step, title, description]) => (
                                <div key={step} className="border-t border-white/20 pt-4">
                                    <span className="font-display text-sm font-bold text-primary-300">{step}</span>
                                    <h4 className="mt-3 font-display text-lg font-bold">{title}</h4>
                                    <p className="mt-2 text-sm leading-6 text-gray-300">{description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

            </div>
            <ServiceModal isOpen={!!activeService} onClose={closeModal} service={activeService} />
        </section>
    );
};

export default Services;
